import React from "react";
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  Slider, TextField, CircularProgress, Alert
} from "@mui/material";
import '../../AssistantSettingsTheme.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const VoiceConfigSettings = ({ values, onChange }) => {
  const voiceConfig = values.voice_config || {};
  const speed = Number(voiceConfig.speed ?? 1.0);
  const [voices, setVoices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  React.useEffect(() => {
    let mounted = true;
    const abortController = new AbortController();
    const fetchVoices = async () => {
      try {
        setLoading(true);
        setError("");
        const resp = await fetch(`${API_BASE_URL}/api/voices`, { signal: abortController.signal });
        if (!resp.ok) {
          throw new Error(`HTTP error! status: ${resp.status}`);
        }
        const data = await resp.json();
        if (mounted) {
          setVoices(data.voices || []);
        }
      } catch (e) {
        if (mounted && e.name !== "AbortError") {
          setError("Could not load voices from ElevenLabs.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchVoices();
    return () => {
      mounted = false;
      abortController.abort();
    };
  }, []); 

  const handleVoiceChange = (id) => {
    onChange("voice_config.voice_id", id);
  };

  return (
    <Box className="assistantSettingsPanel" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="subtitle1">Voice Configuration</Typography>

      <TextField label="Voice Provider" value="elevenlabs" disabled fullWidth />

      {error && <Alert severity="error">{error}</Alert>}
      
      <FormControl fullWidth disabled={loading}>
        <InputLabel id="voice-id-select-label">Voice</InputLabel>
        <Select
          labelId="voice-id-select-label"
          id="voice-id-select"
          value={voiceConfig.voice_id || ""}
          label="Voice"
          onChange={(e) => handleVoiceChange(e.target.value)}
          // Custom render function to show the voice name and a snippet of its ID.
          renderValue={(selectedValue) => {
            if (!selectedValue) return "Select a voice";
            const voice = voices.find(v => v.id === selectedValue);
            return voice ? `${voice.name} (${voice.id.slice(0, 6)}…)` : selectedValue;
          }}
        >
          {loading && (
            <MenuItem disabled>
              <CircularProgress size={18} sx={{ mr: 1 }} /> Loading voices…
            </MenuItem>
          )}
          
          {!loading && !error && voices.length === 0 && (
            <MenuItem disabled>No voices found</MenuItem>
          )}

          {!loading && !error && voices.map((v) => (
            <MenuItem key={v.id} value={v.id}>
              {v.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box className="voice-speed-config">
        <Box className="voice-speed-header">
          <Typography className="voice-speed-title">Voice Speed</Typography>
          <Typography component="span" className="voice-speed-value">
            {speed.toFixed(2)}x
          </Typography>
        </Box>

        <Slider
          className="voice-speed-slider"
          value={speed}
          onChange={(_e, val) => {
            const speed = Array.isArray(val) ? val[0] : val;
            // clamp just in case external state was out of bounds
            const clamped = Math.min(1.2, Math.max(0.7, Number(speed)));
            onChange('voice_config.speed', clamped);
          }}
          step={0.05}
          min={0.7}
          max={1.2}
          valueLabelDisplay="off"
          valueLabelFormat={(v) => `${v.toFixed(2)}x`}
        />
        <Box className="voice-speed-legend" aria-hidden="true">
          <span>0.7x Slow</span>
          <span>1.0x Normal</span>
          <span>1.2x Fast</span>
        </Box>

      </Box>

      {voiceConfig.voice_id && (() => {
        const selectedVoice = voices.find(v => v.id === voiceConfig.voice_id);
        return selectedVoice?.preview_url ? (
          <audio controls src={selectedVoice.preview_url} style={{ width: "100%" }} />
        ) : null;
      })()}
    </Box>
  );
};

export default VoiceConfigSettings;
